import { CheckCircle as CheckCircleIcon, Google as GoogleIcon, Link as LinkIcon } from "@mui/icons-material";
import { Alert, Avatar, Box, Button, CircularProgress, Divider, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useOAuthHealth } from "../../hooks/useOAuthHealth";
import { googleServicesService } from "../../services/GoogleServicesService";

interface ServiceConnectionStepProps {
  onComplete: () => void;
  onSkip?: () => void;
  onRefresh?: () => void;
}

export const ServiceConnectionStep: React.FC<ServiceConnectionStepProps> = ({ onComplete, onSkip, onRefresh }) => {
  const { user } = useAuth();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{
    connected: boolean;
    hasRefreshToken: boolean;
    email?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasCompleted, setHasCompleted] = useState(false);

  // OAuth health check
  const { isPluginHealthy } = useOAuthHealth();
  const [isGoogleHealthy, setIsGoogleHealthy] = useState<boolean | null>(null);

  // Check OAuth health
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const healthy = await isPluginHealthy("GooglePlugin");
        setIsGoogleHealthy(healthy);
      } catch (error) {
        console.error("Failed to check OAuth health:", error);
        setIsGoogleHealthy(false);
      }
    };

    checkHealth();
  }, [isPluginHealthy]);

  React.useEffect(() => {
    // Check if we're returning from Google OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.get("code");
    const hasAuthError = urlParams.get("error");

    if (hasAuthCode) {
      // We're returning from OAuth, show connecting state
      setIsConnecting(true);
    } else if (hasAuthError) {
      setError("Google authentication was cancelled or failed");
    }

    checkConnectionStatus();

    // Check connection status periodically in case user completes OAuth in another tab
    const interval = setInterval(() => {
      checkConnectionStatus(); // Always check status, but don't auto-complete
    }, 5000); // Reduced from 2000ms to 5000ms (5 seconds)

    return () => {
      clearInterval(interval);
    };
  }, [isGoogleHealthy]); // Re-check when OAuth health changes

  const checkConnectionStatus = async () => {
    try {
      // Use OAuth health check instead of Google services status
      const isConnected = isGoogleHealthy === true;

      setConnectionStatus({
        connected: isConnected,
        hasRefreshToken: isConnected,
        email: undefined, // We don't need email for onboarding
      });

      // Clear connecting state when connection is detected
      if (isConnected) {
        setIsConnecting(false);
      }

      // No auto-completion - user must click "Continue to Next Step"
    } catch (error) {
      console.error("ServiceConnectionStep: Error checking connection status:", error);
      setError("Failed to check connection status");
    }
  };

  const handleConnectGoogle = async () => {
    if (!user) {
      setError("User not authenticated");
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Use the same OAuth flow as the Google Services page
      await googleServicesService.requestPermissions();
      // The auth flow will redirect, so we don't need to handle completion here
    } catch (error) {
      console.error("Error initiating Google auth:", error);
      setError("Failed to initiate Google authentication");
      setIsConnecting(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    setIsConnecting(true);
    setError(null);

    try {
      await googleServicesService.clearAndReauthenticate();
      await checkConnectionStatus();
    } catch (error) {
      console.error("Error disconnecting Google:", error);
      setError("Failed to disconnect Google account");
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Box textAlign="center">
      <Box mb={4}>
        <Avatar
          sx={{
            width: 64,
            height: 64,
            mx: "auto",
            mb: 2,
            bgcolor: "primary.main",
          }}
        >
          <LinkIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h5" component="h3" gutterBottom fontWeight="medium">
          Connect Google Services
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: "auto" }}>
          Connect your Google account to enable calendar, email, and drive integration. This allows Costaff.ai to help manage your schedule and access your documents.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {connectionStatus?.connected && connectionStatus?.hasRefreshToken && (
        <Box mb={3}>
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium">
              Google account connected successfully
            </Typography>
            {connectionStatus.email && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                {connectionStatus.email}
              </Typography>
            )}
          </Alert>
        </Box>
      )}

      <Box>
        {connectionStatus?.connected && connectionStatus?.hasRefreshToken ? (
          <Box display="flex" flexDirection="column" gap={2}>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={() => {
                setHasCompleted(true);
                onComplete();
              }}
              fullWidth
              startIcon={<CheckCircleIcon />}
            >
              Continue to Next Step
            </Button>
            <Button variant="outlined" disabled fullWidth startIcon={<GoogleIcon />}>
              Google Account Connected
            </Button>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Button variant="contained" size="large" onClick={handleConnectGoogle} disabled={isConnecting} fullWidth startIcon={isConnecting ? <CircularProgress size={20} /> : <GoogleIcon />}>
              {isConnecting ? "Connecting..." : "Connect Google Account"}
            </Button>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="body2" color="text.secondary" textAlign="center">
        By connecting Google, you allow Costaff.ai to access your calendar, email, and drive to provide personalized assistance.
      </Typography>
    </Box>
  );
};
